import { createSignal, createEffect, onCleanup, onMount, JSX } from 'solid-js';
import { debounce } from 'lodash';
import { apiClient } from '@api/apiClient';
import { CloseIcon } from '@atgfr8/icons';
import { CarrierAutoCompleteModel } from '@typeDefinitions/carrierTypes';

import { carrierStatusStyles } from './helpers';

interface CarrierAutocompleteProps {
  label: string;
  value?: string | number | null | undefined;
  onKeyDown?: (e: { currentTarget: { value: string }; key: string }) => void;
  onSelect?: (option: CarrierAutoCompleteModel) => void;
  placeholder?: string;
  minChars?: number;
  disabled?: boolean;
  error?: string | null;
  pickList?: JSX.Element;
  reset?: number;
  clear?: boolean;
}

const CARRIER_AUTOCOMPLETE_ENDPOINT = 'carrier/autocomplete?term';

export const CarrierAutocomplete = (props: CarrierAutocompleteProps) => {
  createEffect(() => {
    if (props.clear) {
      handleClear();
    }
  });
  const [isOpen, setIsOpen] = createSignal(false);
  const [options, setOptions] = createSignal<CarrierAutoCompleteModel[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [inputValue, setInputValue] = createSignal('');
  const [selectedIndex, setSelectedIndex] = createSignal(-1);
  const [searchError, setSearchError] = createSignal<string | null>(null);

  let inputRef: HTMLInputElement | undefined;
  let dropdownRef: HTMLDivElement | undefined;

  const debouncedSearch = debounce(async (query: string) => {
    const minChars = props.minChars ?? 3;
    if (query.length >= minChars) {
      setLoading(true);
      setSearchError(null);
      setIsOpen(true); // open dropdown so loading spinner is visible
      try {
        const response: {
          value?: [CarrierAutoCompleteModel];
        } = await apiClient.get(
          `${CARRIER_AUTOCOMPLETE_ENDPOINT}=${encodeURIComponent(query)}`,
        );
        const data = response.value;
        setOptions(data ?? []);
        setIsOpen(true); // keep open for results, "No options found", or error
        setSelectedIndex(-1);
      } catch (err) {
        setSearchError('Failed to fetch options');
        setOptions([]);
        // keep dropdown open so error message is visible
        setIsOpen(true);
      } finally {
        setLoading(false);
      }
    } else {
      setOptions([]);
      setIsOpen(false);
    }
  }, 100);

  const handleInputChange = (e: { target: { value: string } }) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    if (newValue.length === 0) {
      setOptions([]);
      setIsOpen(false);
    }
    void debouncedSearch(newValue);
  };

  const handleOptionSelect = (option: CarrierAutoCompleteModel) => {
    setInputValue(option.name);
    setIsOpen(false);
    setSelectedIndex(-1);
    if (props.onSelect) {
      props.onSelect(option);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setOptions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    if (props.onSelect) {
      props.onSelect({
        id: 0,
        name: '',
        mc: '',
        dot: '',
        status: '',
        city: '',
        state: '',
        ltlEnabled: false,
      });
    }
    inputRef?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (isOpen() && options().length > 0) {
          setSelectedIndex((prev) =>
            prev < options().length - 1 ? prev + 1 : prev,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen() && options().length > 0) {
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex() >= 0 && options()[selectedIndex()]) {
          handleOptionSelect(options()[selectedIndex()]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        if (props.onKeyDown) {
          props.onKeyDown({
            currentTarget: { value: inputValue() },
            key: e.key,
          });
        }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef &&
      !dropdownRef.contains(event.target as Node) &&
      inputRef &&
      !inputRef.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  };

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    debouncedSearch.cancel();
  });

  // Scroll selected option into view when navigating with keyboard
  createEffect(() => {
    const idx = selectedIndex();
    if (idx >= 0 && dropdownRef) {
      const el = dropdownRef.querySelector(
        `[data-option-index="${idx}"]`,
      ) as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });

  const borderColor = () =>
    Boolean(props.error) ? 'border-red-500' : 'border-[#80b6cf]';
  const focusBorderColor = () =>
    Boolean(props.error) ? 'focus:border-red-600' : 'focus:border-[#123b50]';
  const hoverBorderColor = () =>
    Boolean(props.error) ? 'hover:border-red-600' : 'hover:border-[#123b50]';

  return (
    <div class="relative">
      <div class="text-sm text-gray-500 font-medium">{props.label}</div>
      <div class="relative">
        <input
          ref={inputRef}
          class={`w-full px-3 py-2 pr-8 border ${borderColor()} rounded-md shadow-xs focus:outline-hidden ${focusBorderColor()} ${hoverBorderColor()} ${
            Boolean(props.disabled) ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
          value={inputValue()}
          onInput={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (options().length > 0 && !Boolean(props.disabled)) {
              setIsOpen(true);
            }
          }}
          placeholder={props.placeholder ?? ''}
          disabled={props.disabled}
        />
        {inputValue() && !Boolean(props.disabled) && (
          <button
            type="button"
            onClick={handleClear}
            class="absolute right-0 top-0 bottom-0 flex items-center justify-center w-8 text-gray-400 hover:text-gray-600 focus:outline-hidden"
          >
            <CloseIcon class="w-4 h-4" />
          </button>
        )}
      </div>

      {Boolean(props.error) && (
        <div class="mt-1 text-sm text-red-500">{props.error}</div>
      )}

      {isOpen() && (
        <div
          ref={dropdownRef}
          class="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#80b6cf] rounded-md shadow-lg max-h-60 overflow-y-auto overflow-x-hidden"
        >
          {loading() && (
            <div class="px-3 py-2 text-sm text-gray-500 flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b border-[#123b50] mr-2"></div>
              Loading...
            </div>
          )}

          {Boolean(searchError()) && (
            <div class="px-3 py-2 text-sm text-red-500">{searchError()}</div>
          )}

          {!loading() &&
            !Boolean(searchError()) &&
            options().length === 0 &&
            inputValue().length >= (props.minChars ?? 3) && (
              <div class="px-3 py-2 text-sm text-gray-500">
                No options found
              </div>
            )}

          {props.pickList}
          {!loading() &&
            !Boolean(searchError()) &&
            options().map((option, index) => (
              <div
                data-option-index={index}
                class={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                  index === selectedIndex() ? 'bg-gray-200 text-black' : ''
                }`}
                onClick={() => handleOptionSelect(option)}
              >
                <div>{option.name}</div>
                <div class="flex items-center gap-1">
                  <div class="text-xs rounded-xl px-2 py-px w-fit y-fit text-center bg-gray-200">
                    MC#{option.mc}
                  </div>
                  <div class="text-xs rounded-xl px-2 py-px w-fit y-fit text-center bg-gray-200">
                    DOT#{option.dot}
                  </div>
                  <div
                    class={`text-xs rounded-xl px-2 py-px w-fit y-fit text-center ${carrierStatusStyles[option.status]}`}
                  >
                    {option.status}
                  </div>
                  <div class="text-xs rounded-xl px-2 py-px w-fit y-fit text-center bg-gray-200">
                    {option.city} , {option.state}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
